import { datadogRum } from "@datadog/browser-rum";

/**
 * Where in the app a signature was collected.
 *
 * Every `SignaturePicker` call site passes one of these, so adoption can be
 * read per workflow rather than as a single global count — "the pad works on
 * the Purchase Agreement but nobody uses it on invoices" is the answer the
 * rollout actually needs, and it is unrecoverable after the fact if the action
 * only says that *a* signature happened.
 */
export type SignatureEntryPoint =
  | "purchase-agreement"
  | "invoice"
  | "report-footer"
  | "custom-print-form"
  | "pdf-form"
  | "appointment-form"
  | "sign-document"
  | "loaner-form"
  | "personal-details";

/** Terminal state of one visit to the signature-pad tab. */
export type SignaturePadOutcome = "confirmed" | "not_found";

export const SIGNATURE_PAD_USED_ACTION = "signature_pad_used";

export interface SignaturePadUsedContext {
  /** Only "topaz" today; kept explicit so a second vendor is a value, not a fork. */
  method: "topaz";
  outcome: SignaturePadOutcome;
  /** Pad model as SigWeb reports it, when it reports one at all. */
  model?: string | null;
  /** SigWeb service version — the other half of a support triage. */
  version?: string | null;
  entryPoint?: SignatureEntryPoint;
}

/**
 * Report one signature-pad outcome to Datadog RUM.
 *
 * Deliberately fire-and-forget and never thrown from: this runs on the
 * signing path, and a telemetry failure must never be the reason a patient
 * cannot sign. `addAction` is already a no-op before RUM initialises, so the
 * guard here is only for a genuinely broken SDK.
 */
export function trackSignaturePadUsed(context: SignaturePadUsedContext): void {
  try {
    datadogRum.addAction(SIGNATURE_PAD_USED_ACTION, {
      method: context.method,
      outcome: context.outcome,
      model: context.model ?? null,
      version: context.version ?? null,
      entry_point: context.entryPoint ?? null
    });
  } catch {
    // Telemetry is never worth breaking a signature over.
  }
}
